import { ConnectErrorMessage } from "@/components/ConnectErrorMessage";
import {
  MessageInitShape,
  DescMessage,
  DescService,
  MessageShape,
  DescMethodUnary,
} from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
// eslint-disable-next-line no-restricted-imports
import { useMutation, UseMutationOptions } from "@connectrpc/connect-query";
import { InventoryService } from "@/gen_src/proto/chalk/server/v1/inventory_pb";
import { mutationAffectsNavbarCounts } from "@/utils/hooks/grpc/navbar-count-services";
import {
  InvalidateQueryFilters,
  Query,
  QueryClientContext,
  UseMutationResult,
} from "@tanstack/react-query";
import { createElement, useCallback, useContext } from "react";
import toast from "react-hot-toast";

// utility function - invalidate all queries belonging to a service
export const makeInvalidationPredicateForService = (service: DescService) => {
  return (query: Query) => {
    const { queryKey } = query;
    const [connectStaticKey, methodDescriptor] = queryKey;

    return (
      connectStaticKey === "connect-query" &&
      methodDescriptor != null &&
      typeof methodDescriptor == "object" &&
      "serviceName" in methodDescriptor &&
      methodDescriptor["serviceName"] === service.typeName
    );
  };
};

// when a mutation fires, invalidate all queries that are in the same serviceName
export const makeInvalidationPredicateForMutation = (mutationMethod: DescMethodUnary) =>
  makeInvalidationPredicateForService(mutationMethod.parent);

/**
 * Returns a callback that invalidates (and refetches, while observed) every
 * cached query of the given service. Use after a mutation whose side effects
 * land outside its own service — e.g. UpdateMonitor rewriting the monitor's
 * owned chart in ChartsService — which useGRPCMutation's same-service
 * auto-invalidation cannot see. Await it before navigating away so still-
 * mounted queries refetch and the cache is fresh for the next visit.
 */
export const useInvalidateServiceQueries = (service: DescService): (() => Promise<void>) => {
  const queryClient = useContext(QueryClientContext);
  return useCallback(async () => {
    await queryClient?.invalidateQueries({
      predicate: makeInvalidationPredicateForService(service),
    });
  }, [queryClient, service]);
};

// utility function - invalidate all queries matching certain rpc calls
export const makeInvalidationPredicateForQueries = (queries: DescMethodUnary[]) => {
  return (query: Query) => {
    const { queryKey } = query;
    const [connectStaticKey, methodDescriptor] = queryKey;

    if (
      connectStaticKey !== "connect-query" ||
      methodDescriptor == null ||
      typeof methodDescriptor != "object" ||
      !("serviceName" in methodDescriptor) ||
      !("methodName" in methodDescriptor)
    ) {
      return false;
    }

    const { serviceName, methodName } = methodDescriptor;

    return queries.some(
      (targetQuery) =>
        targetQuery.parent.typeName === serviceName && targetQuery.name === methodName,
    );
  };
};

/** The toast `useGRPCMutation` shows when a caller does not supply `onError`. */
export const showDefaultGRPCMutationErrorToast = (
  method: DescMethodUnary,
  error: ConnectError,
): void => {
  toast.error(
    createElement(ConnectErrorMessage, {
      error,
      message: `Error making request ${method.parent.name}.${method.name}: ${error.rawMessage}`,
    }),
    {
      style: { overflowWrap: "anywhere" },
    },
  );
};

/** Suppresses the default toast, for call sites that report the failure themselves. */
export const WITHOUT_DEFAULT_ERROR_TOAST = { onError: () => undefined };

export const useGRPCMutation = <I extends DescMessage, O extends DescMessage, Ctx = unknown>(
  method: DescMethodUnary<I, O>,
  options?: UseMutationOptions<I, O, Ctx>,
  // Pass `false` to skip the automatic same-service invalidation, for callers that own cache
  // re-sync themselves (e.g. optimistic updates that invalidate on their own schedule).
  invalidationOptions?: Partial<InvalidateQueryFilters> | false,
): UseMutationResult<MessageShape<O>, ConnectError, MessageInitShape<I>, Ctx> => {
  const queryClient = useContext(QueryClientContext);

  const invalidateOnSuccess = useCallback(
    (...args: Parameters<Required<UseMutationOptions<I, O, Ctx>>["onSuccess"]>) => {
      options?.onSuccess?.(...args);
      if (!queryClient) {
        return;
      }
      if (invalidationOptions !== false) {
        queryClient.invalidateQueries({
          predicate: makeInvalidationPredicateForMutation(method),
          ...invalidationOptions,
        });
      }
      // Ignores the opt-out above: that is for callers re-syncing their own service's
      // cache by hand, and nobody hand-maintains the count badges.
      if (mutationAffectsNavbarCounts(method)) {
        queryClient.invalidateQueries({
          predicate: makeInvalidationPredicateForService(InventoryService),
        });
      }
    },
    [options, queryClient, method, invalidationOptions],
  );

  return useMutation(method, {
    // Intentionally an over-rideable default
    onError: (error) => showDefaultGRPCMutationErrorToast(method, error),
    ...options,
    // Intentionally always applies - internally calls options.onSuccess
    onSuccess: invalidateOnSuccess,
  });
};
